(() => {
  'use strict';

  const FINE_POINTER = '(hover:hover) and (pointer:fine)';
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const finePointer = window.matchMedia(FINE_POINTER);
  const reducedMotion = window.matchMedia(REDUCED_MOTION);
  const controllers = new WeakMap();
  const roots = new Set();
  let raf = 0;
  let pending = null;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const limitsFor = (element) => element.dataset.ldActionFamily === 'category-primary'
    ? { x: 4, y: 3, labelX: 1.6, labelY: 1.2 }
    : { x: 5, y: 4, labelX: 2, labelY: 1.5 };

  const reset = (element) => {
    element.style.setProperty('--ld-action-magnetic-x', '0px');
    element.style.setProperty('--ld-action-magnetic-y', '0px');
    element.style.setProperty('--ld-action-label-x', '0px');
    element.style.setProperty('--ld-action-label-y', '0px');
    element.style.setProperty('--ld-action-press-scale', '1');
  };

  const flush = () => {
    raf = 0;
    const job = pending;
    pending = null;
    if (!job || reducedMotion.matches || !finePointer.matches || !job.element.isConnected) return;
    const { element, event } = job;
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const nx = clamp((event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2), -1, 1);
    const ny = clamp((event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2), -1, 1);
    const limits = limitsFor(element);
    element.style.setProperty('--ld-action-magnetic-x', `${(nx * limits.x).toFixed(2)}px`);
    element.style.setProperty('--ld-action-magnetic-y', `${(ny * limits.y).toFixed(2)}px`);
    element.style.setProperty('--ld-action-label-x', `${(nx * limits.labelX).toFixed(2)}px`);
    element.style.setProperty('--ld-action-label-y', `${(ny * limits.labelY).toFixed(2)}px`);
  };

  const schedule = (element, event) => {
    pending = { element, event };
    if (!raf) raf = window.requestAnimationFrame(flush);
  };

  const enhance = (element) => {
    if (!(element instanceof HTMLElement) || controllers.has(element)) return;
    const abort = new AbortController();
    const signal = abort.signal;
    const magnetic = element.hasAttribute('data-ld-magnetic');

    if (magnetic) {
      element.addEventListener('pointermove', (event) => {
        if (!finePointer.matches || reducedMotion.matches) return;
        schedule(element, event);
      }, { signal, passive: true });
      element.addEventListener('pointerleave', () => reset(element), { signal, passive: true });
    }

    element.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || reducedMotion.matches) return;
      element.style.setProperty('--ld-action-press-scale', '.985');
    }, { signal, passive: true });
    const release = () => element.style.setProperty('--ld-action-press-scale', '1');
    element.addEventListener('pointerup', release, { signal, passive: true });
    element.addEventListener('pointercancel', release, { signal, passive: true });
    element.addEventListener('blur', release, { signal, passive: true });

    element.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (!reducedMotion.matches) element.style.setProperty('--ld-action-press-scale', '.985');
    }, { signal });
    element.addEventListener('keyup', (event) => {
      if (event.key === 'Enter' || event.key === ' ') release();
    }, { signal });

    controllers.set(element, abort);
    roots.add(element);
  };

  const scan = (scope = document) => {
    scope.querySelectorAll?.('[data-ld-action-motion]').forEach(enhance);
  };

  const destroyWithin = (scope) => {
    [...roots].forEach((element) => {
      if (scope === element || scope.contains?.(element)) {
        controllers.get(element)?.abort();
        controllers.delete(element);
        roots.delete(element);
        reset(element);
      }
    });
  };

  const resetAll = () => roots.forEach(reset);
  finePointer.addEventListener?.('change', resetAll);
  reducedMotion.addEventListener?.('change', resetAll);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
